// Shared job-sheet list: the active/archived table of completed jobs and their engineer job
// sheets, each row expandable to the full record. Parameterised by `kind` so the SAME list powers
// two mutually-exclusive views (Session AB, Item 1): Service Records (kind="service": every
// non-call-out sheet) and the Call-outs page's completed-visit records (kind="callout"). A
// call-out sheet is therefore approved on the Call-outs page, never on Service Records.
//
// Session ADE: the expanded-row content moved into the ONE shared ServiceRecordPanel (also used
// inline on the lift detail page), which carries the record, the actions (Download PDF, Send to
// Customer, approval, archive/delete) and INLINE amendment editing (the amend modal is retired).
// This file keeps only the table: rows, sorting, the quick per-row archive/delete, and the
// collapse guard for unsaved panel edits.
// Session AGZ: below md the list renders as a CARD LIST (full ELE reference, site, date,
// type + status pills, tap-to-expand) instead of the squeezed three-column table the
// Director's phone screenshot showed (ELE refs ellipsised at 16% width, the pending pill
// spilling its column). The layout is chosen ONCE at mount via initialMobileLayout -- see
// src/lib/mobileLayout.ts for why an either/or beats the PPM dual-render here (the
// expanded ServiceRecordPanel must only ever mount once). jsdom has no matchMedia, so
// every pre-AGZ test still renders the table it pins.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { JobSheetListItem, JobSheetListSortKey, JobSheetSlimItem } from "../../lib/api";
import { Pager } from "../../components/Pager";
import JobTypeBadge from "../../components/JobTypeBadge";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { lateSubmissionDays } from "../../lib/visitDateWindow";
import { formatDate } from "../../lib/format";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ServiceRecordPanel, DiscardChangesDialog } from "./ServiceRecordPanel";
import { useSortableColumn } from "../../lib/useSortableColumn";
import { ExportCsvMenu } from "../../components/ExportCsvMenu";
import { SortableHeader } from "../../components/SortableHeader";
import { initialMobileLayout } from "../../lib/mobileLayout";

// Session SC2B: the list is PAGED on the server (it was 9,844 rows and 96 MB at 1,000 lifts), so the sort runs there too: the SORT RULE
// keeps a sort client-side only for a list that is not paginated. The column keys are the server's whitelist (jobSheetListPaging.ts);
// completed sorts by the real date, defects numerically, empties last, text case-insensitive and numeric-aware, as before.
const SORT_KEYS: ReadonlySet<string> = new Set<JobSheetListSortKey>(["lift", "type", "site", "completed", "defects"]);
const PAGE_SIZE = 50;

// A stable row identity for the open-row + React key, so sorting never mixes up which sheet is
// expanded (jobId is the de-facto identity used throughout; ref is a defensive fallback).
const sheetKey = (s: Pick<JobSheetSlimItem, "jobId" | "ref">): string => s.jobId ?? `ref:${s.ref}`;

// The slim row of a full record, for a deep-linked sheet that is not on the loaded page.
function toSlim(f: JobSheetListItem): JobSheetSlimItem {
  const hasWorkContent =
    f.checklist.length > 0 || f.defects.length > 0 || f.partsUsed.length > 0 || !!f.advisoryNotes?.trim() || !!f.engineerNotes?.trim() || !!f.faultFound?.trim() || !!f.workCarriedOut?.trim();
  return {
    jobId: f.jobId, ref: f.ref, reference: f.reference, jobType: f.jobType, jobStatus: f.jobStatus, assetType: f.assetType, siteName: f.siteName, siteId: f.siteId,
    assetId: f.assetId, customerId: f.customerId, customerName: f.customerName, completedAt: f.completedAt, receivedAt: f.receivedAt, emailSentAt: f.emailSentAt,
    emailSentTo: f.emailSentTo, driveFileUrl: f.driveFileUrl, defectsCount: f.defectsCount, equipmentOutOfService: f.equipmentOutOfService, archivedAt: f.archivedAt,
    sentToClientAt: f.sentToClientAt, invoiced: f.invoiced, linkedInvoice: f.linkedInvoice, isCallout: f.isCallout, riskAssessmentMissing: f.riskAssessmentMissing,
    // A full record knows whether its approval was ever reopened (FR4), so the deep-linked row agrees with the panel.
    lateReason: f.lateReason, willArchive: f.completedAt != null || f.sentToClientAt != null || f.invoiced || hasWorkContent || f.approvalReopened === true,
  };
}

// The full record an opened row shows (GET /api/jobsheets/:jobId): fetched when the row is opened, because the list rows are slim.
type FullState = { state: "loading" } | { state: "error" } | { state: "ready"; sheet: JobSheetListItem };

// Whole UTC calendar days since completion, for the "N days old" approval-ageing marker (Session
// AQS, W-8). UTC calendar days, matching the platform's own daysOverdue (morningActions.ts) rather
// than a raw 24h-bucket count, so "today" reads 0 the moment it is completed rather than counting
// down from midnight.
function daysOld(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const startOfUtcDay = (x: Date) => Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
  return Math.max(0, Math.floor((startOfUtcDay(new Date()) - startOfUtcDay(d)) / (24 * 60 * 60 * 1000)));
}

function ageLabel(days: number): string {
  return days === 0 ? "Today" : days === 1 ? "1 day old" : `${days} days old`;
}

export function JobSheetList({
  kind,
  deepLinkParam = "job",
  emptyActiveText,
  emptyArchivedText,
}: {
  // Which view this list renders. "service" = every non-call-out sheet (Service Records);
  // "callout" = reactive call-out visits (Call-outs page).
  kind: "service" | "callout";
  // The URL search param a deep link uses to auto-expand a specific sheet by jobId.
  deepLinkParam?: string;
  emptyActiveText: string;
  emptyArchivedText: string;
}) {
  const [sheets, setSheets] = useState<JobSheetSlimItem[]>([]);
  // The true number of matching records on the server, never the length of the page (Session SC2B).
  const [total, setTotal] = useState(0);
  // How many of the rows belong to the server's page (a deep-linked record fetched by id is shown first and is not one of them).
  const [pageCount, setPageCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ).
  const [mobileCards] = useState(initialMobileLayout);
  // The expanded sheet is tracked by its stable identity (not its array index) so re-sorting the list
  // never expands the wrong row (Session AU, Item 3).
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [view, setView] = useState<"active" | "archived">("active");
  // The full records of the rows that were opened (the list rows are slim), by job id.
  const [fulls, setFulls] = useState<Record<string, FullState>>({});
  // A deep-linked job with no record to show keeps the "not recorded yet" note.
  const [deepMissing, setDeepMissing] = useState(false);
  // A page may deep-link here with ?<deepLinkParam>=<jobId> to open a specific sheet.
  const [searchParams] = useSearchParams();
  const jobParam = searchParams.get(deepLinkParam);

  const sort = useSortableColumn(`jobsheets:${kind}`);
  // Session AQS, W-8: the approval queue's own default is oldest-completed-first, not "whatever
  // order the API happened to return" (useSortableColumn's own null-key default). The office can
  // still pick any other column -- that click is honoured and persisted exactly as before -- this
  // only decides what the page opens on before anyone has touched a header.
  const sortKey: JobSheetListSortKey = sort.sortKey && SORT_KEYS.has(sort.sortKey) ? (sort.sortKey as JobSheetListSortKey) : "completed";
  const sortDir: "asc" | "desc" = sort.sortKey && SORT_KEYS.has(sort.sortKey) ? sort.sortDir : "asc";

  // Unsaved-edit guard for collapsing the open panel (Session ADE): the panel reports its dirty
  // state; a collapse (row click, view switch) while dirty asks before discarding. Route
  // navigation and reload/close are guarded inside the panel itself.
  const dirtyRef = useRef(false);
  const [pendingCollapse, setPendingCollapse] = useState<(() => void) | null>(null);
  const requestCollapse = useCallback((collapse: () => void) => {
    if (dirtyRef.current) setPendingCollapse(() => collapse);
    else collapse();
  }, []);

  const fetchPage = useCallback(
    (v: "active" | "archived", off: number) =>
      api.jobsheets.listPage({ archived: v === "archived" ? "archived" : undefined, kind, limit: PAGE_SIZE, offset: off, sort: sortKey, dir: sortDir }),
    [kind, sortKey, sortDir],
  );

  const loadFull = useCallback((jobId: string) => {
    setFulls((f) => ({ ...f, [jobId]: { state: "loading" } }));
    api.jobsheets
      .get(jobId)
      .then((sheet) => setFulls((f) => ({ ...f, [jobId]: { state: "ready", sheet } })))
      .catch(() => setFulls((f) => ({ ...f, [jobId]: { state: "error" } })));
  }, []);

  const load = useCallback(
    (v: "active" | "archived", off = 0) => {
      let live = true;
      setLoading(true);
      setError(false);
      setOpenKey(null);
      setDeepMissing(false);
      setFulls({});
      dirtyRef.current = false;
      fetchPage(v, off)
        .then(async (page) => {
          if (!live) return;
          let rows = page.items;
          setTotal(page.total);
          setPageCount(page.items.length);
          setOffset(off);
          // Auto-expand the deep-linked sheet (by jobId) when present and on file. One that is not on this page is fetched by id
          // (it can be anywhere in thousands of records) and shown first; one with no record at all keeps the "not recorded" note.
          if (jobParam && v === "active" && off === 0) {
            if (rows.some((r) => r.jobId === jobParam)) {
              setOpenKey(jobParam);
              loadFull(jobParam);
            } else {
              try {
                const full = await api.jobsheets.get(jobParam);
                if (!live) return;
                if (full.archivedAt == null && full.isCallout === (kind === "callout")) {
                  rows = [toSlim(full), ...rows];
                  setFulls({ [jobParam]: { state: "ready", sheet: full } });
                  setOpenKey(jobParam);
                } else setDeepMissing(true);
              } catch {
                if (live) setDeepMissing(true);
              }
            }
          }
          if (live) setSheets(rows);
        })
        .catch(() => {
          if (live) setError(true);
        })
        .finally(() => {
          if (live) setLoading(false);
        });
      return () => {
        live = false;
      };
    },
    [jobParam, kind, fetchPage, loadFull],
  );

  // A change of view or sort starts again at the first page.
  useEffect(() => load(view, 0), [load, view]);

  // Reload the current page but KEEP a given row expanded (used after an amendment, so the office
  // stays on the sheet it just corrected and sees the "amended since sent" prompt update in place).
  const reloadKeepingOpen = useCallback(
    (jobId: string) => {
      setLoading(true);
      setError(false);
      fetchPage(view, offset)
        .then((page) => {
          setSheets(page.items);
          setTotal(page.total);
          setPageCount(page.items.length);
          const stays = page.items.some((r) => r.jobId === jobId);
          setOpenKey(stays ? jobId : null);
          if (stays) loadFull(jobId);
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    },
    [view, offset, fetchPage, loadFull],
  );

  const goToOffset = useCallback(
    (off: number) => {
      requestCollapse(() => {
        load(view, off);
      });
    },
    [requestCollapse, load, view],
  );

  // Apply a change to a row and to its full record, so the opened panel and the row agree (a send or an approval from the panel).
  const patchRow = useCallback((jobId: string, patch: Partial<JobSheetListItem>) => {
    setSheets((rows) => rows.map((r) => (r.jobId === jobId ? ({ ...r, ...patch } as JobSheetSlimItem) : r)));
    setFulls((f) => {
      const cur = f[jobId];
      return cur && cur.state === "ready" ? { ...f, [jobId]: { state: "ready", sheet: { ...cur.sheet, ...patch } } } : f;
    });
  }, []);
  const onSentRow = useCallback(
    (jobId: string, sentAt: string, emailSentTo?: string | null) => patchRow(jobId, { sentToClientAt: sentAt, ...(emailSentTo !== undefined ? { emailSentTo } : {}) }),
    [patchRow],
  );
  const onApprovedRow = useCallback(
    (jobId: string, res: { driveFileUrl: string | null; emailSentTo: string | null }) =>
      patchRow(jobId, { jobStatus: "complete", emailSentAt: new Date().toISOString(), emailSentTo: res.emailSentTo, driveFileUrl: res.driveFileUrl }),
    [patchRow],
  );

  // Open/close a sheet through the unsaved-edit guard: opening ANOTHER row also collapses
  // the current one, so both paths run through requestCollapse. Shared by the table rows
  // and the mobile cards so the two layouts cannot drift on the guard.
  const toggleSheet = useCallback(
    (s: JobSheetSlimItem) => {
      const key = sheetKey(s);
      if (openKey === key) requestCollapse(() => setOpenKey(null));
      else
        requestCollapse(() => {
          setOpenKey(key);
          if (s.jobId && fulls[s.jobId]?.state !== "ready") loadFull(s.jobId);
        });
    },
    [openKey, requestCollapse, fulls, loadFull],
  );

  const sorted = sheets; // the server has put them in order

  // A deep link (?<deepLinkParam>=<jobId>) can point at a job that has NO service record yet (an
  // outstanding scheduled / in-progress visit linked from the dashboard's Outstanding Jobs panel).
  // Such a job never appears in this list, so say so honestly rather than silently showing nothing
  // for it. Only for the active view (the deep link targets the active list).
  const deepLinkMissing = jobParam != null && !loading && !error && view === "active" && deepMissing;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap gap-2">
        {(["active", "archived"] as const).map((v) => (
          <button
            key={v}
            onClick={() => requestCollapse(() => setView(v))}
            className={`rounded-ele border px-4 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              view === v ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {v === "active" ? "Active" : "Archived"}
          </button>
        ))}
      </div>
      {/* Session GAP9: the CSV of this list as it is showing (active or archived, this kind, this sort), in the More actions menu. */}
      <ExportCsvMenu list="service-records" params={{ ...(view === "archived" ? { archived: "archived" } : {}), kind, sort: sortKey, dir: sortDir }} />
      </div>

      {deepLinkMissing && (
        <p role="status" className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
          That job has not been recorded yet, so there is no service record to show here. It will appear once the engineer
          submits their sheet. See it on the{" "}
          <Link to="/office/scheduling" className="text-ele-green underline-offset-2 hover:underline">
            Scheduling calendar
          </Link>
          .
        </p>
      )}

      {loading ? (
        <Loader />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the records. Please try again in a moment.
        </p>
      ) : sheets.length === 0 ? (
        <EmptyState message={view === "archived" ? emptyArchivedText : emptyActiveText} />
      ) : mobileCards ? (
        /* Session AGZ mobile card list: full untruncated ELE reference, wrapping site name,
           date, the locked type colour and canonical status pills; the whole card is the
           tap target and expands to the SAME ServiceRecordPanel in place. Maps over the
           same `sorted` array, so any sort set on the desktop table carries over. */
        <ul className="space-y-3">
          {sorted.map((s) => (
            <CardRow
              key={sheetKey(s)}
              sheet={s}
              open={openKey === sheetKey(s)}
              onToggle={() => toggleSheet(s)}
              full={s.jobId ? fulls[s.jobId] : undefined}
              onRetryFull={() => s.jobId && loadFull(s.jobId)}
              onSent={onSentRow}
              onApproved={onApprovedRow}
              onChanged={() => load(view, offset)}
              onAmended={(jobId) => reloadKeepingOpen(jobId)}
              onDirtyChange={(d) => {
                if (openKey === sheetKey(s)) dirtyRef.current = d;
              }}
            />
          ))}
        </ul>
      ) : (
        /* NO overflow-hidden on the table wrapper either (same review finding): it clipped
           the LAST row's expanded More menu on desktop; the office thead is transparent, so
           the class was only rounding corners (the AEW resolution). */
        <div className="rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "16%" }} />
              <col style={{ width: "22%" }} />
              <col className="max-md:hidden" style={{ width: "26%" }} />
              <col style={{ width: "16%" }} />
              <col className="max-md:hidden" style={{ width: "9%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              {/* Mobile-only disclosure chevron: the row already expands in place (a job sheet
                  has no separate detail page), so the chevron just signals that tap. */}
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Lift" colKey="lift" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Type" colKey="type" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Completed" colKey="completed" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Defects" colKey="defects" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                <th className="cell-actions max-md:hidden px-3 py-2"></th>
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => {
                const open = openKey === sheetKey(s);
                return (
                  <FragmentRow
                    key={sheetKey(s)}
                    sheet={s}
                    open={open}
                    onToggle={() => toggleSheet(s)}
                    full={s.jobId ? fulls[s.jobId] : undefined}
                    onRetryFull={() => s.jobId && loadFull(s.jobId)}
                    onSent={onSentRow}
                    onApproved={onApprovedRow}
                    onChanged={() => load(view, offset)}
                    onAmended={(jobId) => reloadKeepingOpen(jobId)}
                    onDirtyChange={(d) => {
                      if (open) dirtyRef.current = d;
                    }}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && total > 0 && (
        <Pager offset={offset} total={total} shown={pageCount} pageSize={PAGE_SIZE} onOffset={goToOffset} noun={["record", "records"]} />
      )}

      <DiscardChangesDialog
        open={pendingCollapse != null}
        onKeep={() => setPendingCollapse(null)}
        onDiscard={() => {
          dirtyRef.current = false;
          pendingCollapse?.();
          setPendingCollapse(null);
        }}
      />
    </div>
  );
}

// The expanded content of a row: the full record, fetched when the row was opened (the list rows are slim).
function OpenedRecord({
  full,
  onRetry,
  onSent,
  onApproved,
  onChanged,
  onAmended,
  onDirtyChange,
}: {
  full: FullState | undefined;
  onRetry: () => void;
  onSent: (jobId: string, sentAt: string, emailSentTo?: string | null) => void;
  onApproved: (jobId: string, res: { driveFileUrl: string | null; emailSentTo: string | null }) => void;
  onChanged: () => void;
  onAmended: (jobId: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  if (!full || full.state === "loading") return <Loader />;
  if (full.state === "error")
    return (
      <p role="alert" className="text-sm text-ele-strong">
        Could not load this record.{" "}
        <button type="button" onClick={onRetry} className="text-ele-green underline-offset-2 hover:underline max-sm:min-h-11">
          Try again
        </button>
      </p>
    );
  return <ServiceRecordPanel sheet={full.sheet} onSent={onSent} onApproved={onApproved} onChanged={onChanged} onAmended={onAmended} onDirtyChange={onDirtyChange} />;
}

function FragmentRow({
  sheet,
  full,
  onRetryFull,
  open,
  onToggle,
  onSent,
  onApproved,
  onChanged,
  onAmended,
  onDirtyChange,
}: {
  sheet: JobSheetSlimItem;
  full: FullState | undefined;
  onRetryFull: () => void;
  open: boolean;
  onToggle: () => void;
  onSent: (jobId: string, sentAt: string, emailSentTo?: string | null) => void;
  onApproved: (jobId: string, res: { driveFileUrl: string | null; emailSentTo: string | null }) => void;
  onChanged: () => void;
  onAmended: (jobId: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const archived = sheet.archivedAt != null;
  // Mirror the server's retain logic for accurate dialog framing (amber Archive vs red Delete).
  const willArchive = sheet.willArchive;
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function confirmDelete() {
    if (!sheet.jobId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.jobsheets.remove(sheet.jobId);
      setShowDelete(false);
      onChanged();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the record. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <tr className="group cursor-pointer border-t border-ele-purple-accent hover:bg-ele-surface" onClick={onToggle}>
        <td className="px-3 py-2 align-top font-heading text-ele-strong">{sheet.reference ?? sheet.ref}</td>
        {/* Session AGZ sentence case: the type is a data label ("Service", "Call-out"),
            not a pill or column header, so the caps display override is gone. */}
        <td className="cell-actions px-3 py-2 align-top">
          <JobTypeBadge type={sheet.jobType} size="sm" />
          <span className="block text-xs text-ele-body opacity-70">{sheet.assetType}</span>
        </td>
        <td className="max-md:hidden px-3 py-2 align-top">
          {/* Forward click-through to the site (Session ADI). The row toggles the record panel open;
              stopPropagation so clicking the site name navigates instead of expanding. This cell is
              max-md:hidden, so the mobile tap-to-expand target is unaffected. The panel header (ADH)
              still carries the full lift + site + customer links once the row is open. */}
          {sheet.siteId ? (
            <Link
              to={`/office/sites/${encodeURIComponent(sheet.siteId)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-ele-green underline-offset-2 hover:underline"
            >
              {sheet.siteName}
            </Link>
          ) : (
            sheet.siteName
          )}
        </td>
        <td className="cell-actions px-3 py-2 align-top">
          {formatDate(sheet.completedAt)}
          {/* Session AGZ: the row chips join the canonical .ele-pill system (they were
              hand-rolled amber-500/danger tints, the pill-fragmentation debt the AEE audit
              flagged), matching the mobile cards below. Each pill sits on its OWN line
              under the date: this cell is .cell-actions (overflow visible, nowrap), so an
              inline pill beside the date would paint past the 16% column into Defects --
              the exact AEW spill the old inline chips already caused. */}
          {sheet.jobStatus === "pending_approval" && (
            <span className="mt-1 block">
              <StatusPill domain="job" status="pending_approval" />
              {/* Session AQS, W-8: the age was previously readable only by comparing the
                  Completed date to today by eye. A live example ran 8 waiting, 3 of them over
                  six weeks, indistinguishable at a glance from one waiting two days. */}
              {(() => {
                const age = daysOld(sheet.completedAt);
                return age != null ? <span className="ml-1.5 text-xs text-ele-body opacity-70">{ageLabel(age)}</span> : null;
              })()}
            </span>
          )}
          {/* Session BBC: sent in more than a week after the visit (the engineer gave a reason). */}
          {sheet.jobStatus === "pending_approval" && lateSubmissionDays(sheet.completedAt, sheet.receivedAt ?? null) != null && (
            <span className="mt-1 block" title={sheet.lateReason ? `Reason: ${sheet.lateReason}` : undefined}>
              <StatusPill word={`Late: sent ${lateSubmissionDays(sheet.completedAt, sheet.receivedAt ?? null)} days after`} tone="amber" />
            </span>
          )}
          {sheet.equipmentOutOfService && (
            <span className="mt-1 block">
              <StatusPill word="Out of service" tone="red" />
            </span>
          )}
        </td>
        <td className={`max-md:hidden px-3 py-2 text-right align-top ${sheet.defectsCount > 0 ? "text-ele-danger" : "text-ele-body"}`}>
          {sheet.defectsCount}
        </td>
        <td className="cell-actions max-md:hidden px-3 py-2 align-top">
          {/* Session ATR: this column is only colgroup's 11% (~106px at common widths), too narrow
              for "Open / Archive / View" plus two 12px gaps to fit on one line. .cell-actions sets
              overflow:visible so the row is never clipped, but with no wrap, justify-end's overflow
              bled off the START (left) edge -- confirmed live by direct DOM measurement: the "Open"
              link's own rect started ~40px left of its flex parent's rect, painting over the
              Defects column's digit behind it (the Director's reported "ghost 0 behind Open"). Same
              mechanism as the Scheduling modal footer fix (ModalShell.tsx, Session ATP): add
              flex-wrap so the overflow wraps onto a second line inside the cell instead of bleeding
              into the neighbouring column. The row already has spare height from the Type/status
              cells, so two lines here fit inside the existing row height. */}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {/* Session AQW (W-7): the record's own detail page (/office/jobsheets/:jobId), in
                addition to the row's own expand-in-place panel below -- the page is what the
                calendar, lift history, search and dashboard now deep-link to. stopPropagation so
                the click navigates instead of toggling this row's own panel open. */}
            {sheet.jobId && (
              <Link
                to={`/office/jobsheets/${encodeURIComponent(sheet.jobId)}`}
                onClick={(e) => e.stopPropagation()}
                className="font-menu text-xs tracking-wide text-ele-blue transition hover:underline"
              >
                Open
              </Link>
            )}
            {/* Delete/archive straight from the row, without expanding it: the most
                common reason to open a sheet is to remove a test or mistaken record.
                The label mirrors the server's archive-vs-hard outcome (amber Archive
                for a retained record, red Delete for a content-free stub). Archived
                rows keep their Reactivate control in the expanded panel only. */}
            {!archived && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setDeleteError(null); setShowDelete(true); }}
                className={`font-menu text-xs tracking-wide transition hover:underline ${
                  willArchive ? "text-amber-400" : "text-ele-danger"
                }`}
              >
                {willArchive ? "Archive" : "Delete"}
              </button>
            )}
            <span className="font-menu text-xs tracking-wide text-ele-green">{open ? "Hide" : "View"}</span>
          </div>
          {/* The confirm dialog lives on the always-visible row so a sheet can be
              archived/deleted from the list without expanding it. Stop the backdrop
              click bubbling up to the row's expand/collapse toggle. */}
          <div onClick={(e) => e.stopPropagation()}>
            <ConfirmDeleteModal
              open={showDelete}
              variant={willArchive ? "archive" : "destructive"}
              title={willArchive ? "Archive this record?" : "Delete this record?"}
              message={
                willArchive
                  ? "This record is completed (or has been invoiced or sent), so it will be archived and hidden from the active list, and can be reactivated."
                  : "This record has not been completed, invoiced or sent, so it will be permanently deleted along with its photos. If it has been, it will be archived instead."
              }
              confirmLabel={willArchive ? "Archive record" : "Delete record"}
              busy={deleting}
              error={deleteError}
              onConfirm={confirmDelete}
              onCancel={() => { if (!deleting) { setShowDelete(false); setDeleteError(null); } }}
            />
          </div>
        </td>
        {/* Mobile-only disclosure chevron: the row already expands in place on tap. */}
        <td className="cell-actions md:hidden pr-1 text-right">
          <svg
            aria-hidden
            viewBox="0 0 16 16"
            className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green"
          >
            <path d="M6 4l4 4-4 4z" />
          </svg>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-ele-purple-accent bg-ele-surface">
          <td colSpan={7} className="cell-wrap px-4 py-4">
            <OpenedRecord
              full={full}
              onRetry={onRetryFull}
              onSent={onSent}
              onApproved={onApproved}
              onChanged={onChanged}
              onAmended={onAmended}
              onDirtyChange={onDirtyChange}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// Session AGZ: one sheet as a mobile card. The full reference renders in monospace and is
// NEVER truncated (the exact defect the Director's screenshot showed); the site wraps; the
// locked jobType colour carries the type; the status is a canonical .ele-pill. Tapping the
// card expands the SAME ServiceRecordPanel in place through the same dirty-collapse guard
// as a table row. The row-level quick Archive/Delete stays a desktop affordance -- on a
// phone the expanded panel carries every action, keeping the card one clean tap target.
function CardRow({
  sheet,
  full,
  onRetryFull,
  open,
  onToggle,
  onSent,
  onApproved,
  onChanged,
  onAmended,
  onDirtyChange,
}: {
  sheet: JobSheetSlimItem;
  full: FullState | undefined;
  onRetryFull: () => void;
  open: boolean;
  onToggle: () => void;
  onSent: (jobId: string, sentAt: string, emailSentTo?: string | null) => void;
  onApproved: (jobId: string, res: { driveFileUrl: string | null; emailSentTo: string | null }) => void;
  onChanged: () => void;
  onAmended: (jobId: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  return (
    // NO overflow-hidden here (adversarial-review HIGH): the expanded panel's More menu is
    // an absolute dropdown that opens past the card's bottom edge, and an overflow-hidden
    // wrapper clips it to an untappable sliver -- on a phone that menu is the ONLY route to
    // Send email / Archive / Delete / Return to engineer (the AEW clipped-menu class).
    <li className="rounded-ele border border-ele-purple-accent bg-ele-surface">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex min-h-[44px] w-full items-start justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block font-mono text-sm font-semibold text-ele-strong">{sheet.reference ?? sheet.ref}</span>
          <span className="block break-words text-sm text-ele-body">{sheet.siteName}</span>
          <span className="mt-0.5 block text-xs text-ele-body opacity-70">{formatDate(sheet.completedAt)}</span>
        </span>
        <span className="flex max-w-[50%] flex-none flex-col items-end gap-1.5">
          <JobTypeBadge type={sheet.jobType} size="sm" />
          {sheet.jobStatus === "pending_approval" ? (
            <span className="flex flex-col items-end gap-0.5">
              <StatusPill domain="job" status="pending_approval" />
              {(() => {
                const age = daysOld(sheet.completedAt);
                return age != null ? <span className="text-xs text-ele-body opacity-70">{ageLabel(age)}</span> : null;
              })()}
            </span>
          ) : sheet.jobStatus === "complete" ? (
            <StatusPill domain="job" status="complete" />
          ) : null}
          {sheet.jobStatus === "pending_approval" && lateSubmissionDays(sheet.completedAt, sheet.receivedAt ?? null) != null && (
            <StatusPill word={`Late: sent ${lateSubmissionDays(sheet.completedAt, sheet.receivedAt ?? null)} days after`} tone="amber" />
          )}
          {sheet.equipmentOutOfService && <StatusPill word="Out of service" tone="red" />}
          <svg
            aria-hidden
            viewBox="0 0 16 16"
            className={`h-4 w-4 fill-current text-ele-purple transition-transform ${open ? "rotate-90" : ""}`}
          >
            <path d="M6 4l4 4-4 4z" />
          </svg>
        </span>
      </button>
      {/* Session AQW (W-7): the record's own detail page, a sibling of the toggle button above
          (never nested inside it -- a Link inside a button is invalid). Always shown, not just
          while expanded, matching the desktop row's own always-visible Open link. */}
      {sheet.jobId && (
        <div className="border-t border-ele-purple-accent px-4 py-1.5 text-right">
          <Link to={`/office/jobsheets/${encodeURIComponent(sheet.jobId)}`} className="font-menu text-xs tracking-wide text-ele-blue hover:underline">
            Open record
          </Link>
        </div>
      )}
      {open && (
        <div className="border-t border-ele-purple-accent px-3 py-4">
          <OpenedRecord
            full={full}
            onRetry={onRetryFull}
            onSent={onSent}
            onApproved={onApproved}
            onChanged={onChanged}
            onAmended={onAmended}
            onDirtyChange={onDirtyChange}
          />
        </div>
      )}
    </li>
  );
}
